/**
 * Project Page — Flows 7 + 8, restyled with Tailwind + Radix Tabs.
 *
 * Layout:
 *   - Sticky header with project name and (when present) client.
 *   - 4-stat counter row.
 *   - Latest-summary card if the worker produced one.
 *   - Radix `<Tabs>` (Notes / Action items / Decisions / Risks / Files),
 *     active tab persisted in `?tab=`.
 *
 * BackButton is provided by `useBackButton` (native Telegram). No
 * in-page back button — the chat-first redesign relies on the
 * platform chrome.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as Tabs from "@radix-ui/react-tabs";
import { useCallback, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ApiError } from "../api/client";
import {
  getProjectDetail,
  listDecisions,
  listNotes,
  listRisks,
  listTasks,
} from "../api/projectMemory";
import { deleteProject, renameProject } from "../api/projects";
import { editTask, updateTaskStatus, type TaskEdit } from "../api/tasks";
import { haptic } from "../hooks/useHaptic";
import { EmptyState } from "../components/EmptyState";
import { IconButton } from "../components/IconButton";
import {
  AlertIcon,
  CheckCircleIcon,
  CheckIcon,
  ChecklistIcon,
  FileIcon,
  NoteIcon,
  PencilIcon,
  TrashIcon,
  UsersIcon,
  XIcon,
} from "../components/icons";
import { LoadingScreen } from "../components/LoadingScreen";
import { Markdown } from "../components/Markdown";
import { RelativeTime } from "../components/RelativeTime";
import { TeamTab } from "../components/TeamTab";
import { useBackButton } from "../hooks/useBackButton";
import type {
  Decision,
  Note,
  NoteSourceType,
  ProjectDetail,
  Risk,
  RiskSeverity,
  Task,
  TaskPriority,
  TaskStatus,
} from "../lib/types";

type TabValue = "notes" | "tasks" | "decisions" | "risks" | "files" | "team";

const ALL_TABS: ReadonlyArray<TabValue> = [
  "notes",
  "tasks",
  "decisions",
  "risks",
  "files",
  "team",
];

function isTabValue(value: string | null): value is TabValue {
  return value !== null && (ALL_TABS as ReadonlyArray<string>).includes(value);
}

const SOURCE_LABEL: Record<NoteSourceType, string> = {
  voice: "voice",
  video_note: "video",
  text: "text",
  forward: "forward",
  pdf: "PDF",
  docx: "DOCX",
  photo: "photo",
};

const PRIORITY_BG: Record<TaskPriority, string> = {
  low: "var(--color-secondary-bg)",
  medium: "var(--color-link)",
  high: "var(--color-destructive)",
};

const SEVERITY_BG: Record<RiskSeverity, string> = {
  low: "var(--color-secondary-bg)",
  medium: "var(--color-link)",
  high: "var(--color-destructive)",
};

function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  return text.slice(0, max).trimEnd() + "…";
}

function describeError(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "Unknown error";
}

/* -------------------------------------------------------------------------- */

export function ProjectPage(): React.ReactElement {
  const { id: projectId = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  useBackButton(useCallback(() => navigate(-1), [navigate]));

  const tabParam = searchParams.get("tab");
  const activeTab: TabValue = isTabValue(tabParam) ? tabParam : "notes";

  const setActiveTab = (value: string): void => {
    if (!isTabValue(value)) return;
    const next = new URLSearchParams(searchParams);
    next.set("tab", value);
    setSearchParams(next, { replace: true });
  };

  const detail = useQuery<ProjectDetail>({
    queryKey: ["project", projectId],
    queryFn: ({ signal }) => getProjectDetail(projectId, signal),
    enabled: projectId.length > 0,
  });

  if (!projectId) {
    return (
      <CenteredError
        title="Project not found"
        onPrimary={() => navigate("/")}
        primaryLabel="Back to chat"
      />
    );
  }
  if (detail.isLoading) return <LoadingScreen message="Loading project..." />;
  if (detail.isError || !detail.data) {
    return (
      <CenteredError
        title="Could not load project"
        message={describeError(detail.error)}
        onPrimary={() => void detail.refetch()}
        primaryLabel="Retry"
      />
    );
  }

  const project = detail.data;
  const counts: ReadonlyArray<[TabValue, string, number]> = [
    ["notes", "Notes", project.notes_count],
    ["tasks", "Action items", project.open_tasks_count],
    ["decisions", "Decisions", project.decisions_count],
    ["risks", "Risks", project.risks_count],
    ["files", "Files", project.links.length],
    ["team", "Team", project.members.length],
  ];

  return (
    <div
      className="min-h-dvh"
      style={{ background: "var(--color-bg)", color: "var(--color-text)" }}
    >
      <Header name={project.name} clientName={project.client_name} />

      <div className="px-4 pt-3 pb-2 space-y-4">
        {project.description ? (
          <p className="text-sm" style={{ color: "var(--color-text)" }}>
            {project.description}
          </p>
        ) : null}

        {project.latest_summary ? (
          <div
            className="rounded-xl p-3"
            style={{ background: "var(--color-secondary-bg)" }}
          >
            <div
              className="text-[11px] uppercase tracking-wide mb-1"
              style={{ color: "var(--color-hint)" }}
            >
              Latest summary
            </div>
            <Markdown text={project.latest_summary} variant="card" />
          </div>
        ) : null}

      </div>

      <Tabs.Root
        value={activeTab}
        onValueChange={setActiveTab}
        className="flex flex-col"
      >
        {/* The analytics tiles double as the section navigation — tapping
            one switches the panel below. No separate tab bar (avoids the
            duplicate). */}
        <Tabs.List
          className="grid grid-cols-3 gap-2 px-4 pt-1 pb-2"
          aria-label="Project sections"
        >
          {counts.map(([key, label, count]) => (
            <SectionTile
              key={key}
              value={key}
              label={label}
              count={count}
              icon={SECTION_ICONS[key]}
              active={activeTab === key}
            />
          ))}
        </Tabs.List>

        <div className="px-4 pb-3">
          <ProjectSettings
            projectId={projectId}
            currentName={project.name}
            isOwner={project.is_owner}
          />
        </div>

        <Tabs.Content value="notes" className="px-4 py-3 outline-none">
          <NotesTab projectId={projectId} />
        </Tabs.Content>
        <Tabs.Content value="tasks" className="px-4 py-3 outline-none">
          <TasksTab projectId={projectId} />
        </Tabs.Content>
        <Tabs.Content value="decisions" className="px-4 py-3 outline-none">
          <DecisionsTab projectId={projectId} />
        </Tabs.Content>
        <Tabs.Content value="risks" className="px-4 py-3 outline-none">
          <RisksTab projectId={projectId} />
        </Tabs.Content>
        <Tabs.Content value="files" className="px-4 py-3 outline-none">
          <FilesTab links={project.links} />
        </Tabs.Content>
        <Tabs.Content value="team" className="px-4 py-3 outline-none">
          <TeamTab projectId={projectId} />
        </Tabs.Content>
      </Tabs.Root>
    </div>
  );
}

/* ---------- Header ----------------------------------------------------- */

function Header({
  name,
  clientName,
}: {
  name: string;
  clientName: string | null;
}): React.ReactElement {
  return (
    <header
      className="sticky top-0 z-20 px-4 py-3"
      style={{
        background: "var(--color-bg)",
        borderBottom: "1px solid var(--color-secondary-bg)",
      }}
    >
      <h1 className="text-lg font-semibold truncate">{name}</h1>
      {clientName ? (
        <p className="text-xs" style={{ color: "var(--color-hint)" }}>
          Client: {clientName}
        </p>
      ) : null}
    </header>
  );
}

/* ---------- Section tiles (analytics + navigation in one) -------------- */

const SECTION_ICONS: Record<TabValue, React.ReactNode> = {
  notes: <NoteIcon size={20} />,
  tasks: <ChecklistIcon size={20} />,
  decisions: <CheckCircleIcon size={20} />,
  risks: <AlertIcon size={20} />,
  files: <FileIcon size={20} />,
  team: <UsersIcon size={20} />,
};

/** A big, comfortable tile that shows a section's count AND switches to it
 *  when tapped (a Radix Tabs trigger under the hood). The active section
 *  gets an accent ring + tinted background. */
function SectionTile({
  value,
  label,
  count,
  icon,
  active,
}: {
  value: TabValue;
  label: string;
  count: number;
  icon: React.ReactNode;
  active: boolean;
}): React.ReactElement {
  return (
    <Tabs.Trigger value={value} asChild>
      <button
        type="button"
        className="flex flex-col items-center justify-center gap-1 rounded-[16px] py-3.5 px-1 active:scale-95 transition-transform outline-none"
        style={{
          background: active
            ? "color-mix(in srgb, var(--color-button) 15%, var(--color-bg))"
            : "var(--color-secondary-bg)",
          boxShadow: active
            ? "inset 0 0 0 1.5px var(--color-button)"
            : "inset 0 0 0 1px var(--surface-hairline)",
        }}
      >
        <span
          style={{
            color: active ? "var(--color-button)" : "var(--color-hint)",
          }}
        >
          {icon}
        </span>
        <span className="text-[20px] font-bold leading-none tabular-nums">
          {count}
        </span>
        <span
          className="text-[10px] font-medium uppercase tracking-wide text-center leading-tight"
          style={{ color: "var(--color-hint)" }}
        >
          {label}
        </span>
      </button>
    </Tabs.Trigger>
  );
}

/* ---------- Settings (rename + delete, ADR 0012) ---------------------- */

// Exported for the dev preview harness (`src/preview.tsx`) so the
// rename/delete controls can be eyeballed at mobile width.
export function ProjectSettings({
  projectId,
  currentName,
  isOwner,
}: {
  projectId: string;
  currentName: string;
  isOwner: boolean;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(currentName);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const renameMutation = useMutation({
    mutationFn: (next: string) => renameProject(projectId, next),
    onSuccess: async () => {
      haptic.notify("success");
      setRenaming(false);
      await queryClient.invalidateQueries({ queryKey: ["project", projectId] });
      await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      await queryClient.invalidateQueries({ queryKey: ["feed"] });
    },
    onError: () => haptic.notify("error"),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteProject(projectId),
    onSuccess: async () => {
      haptic.notify("success");
      await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      await queryClient.invalidateQueries({ queryKey: ["feed"] });
      navigate("/");
    },
    onError: () => haptic.notify("error"),
  });

  return (
    <div className="space-y-2.5">
      {renaming ? (
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={name}
            autoFocus
            onChange={(e) => setName(e.target.value)}
            disabled={renameMutation.isPending}
            className="flex-1 min-w-0 rounded-[12px] px-3.5 py-2.5 text-[15px] outline-none"
            style={{
              background: "var(--color-secondary-bg)",
              color: "var(--color-text)",
              boxShadow: "inset 0 0 0 1px var(--surface-hairline)",
            }}
          />
          <IconButton
            label="Save name"
            variant="primary"
            size={42}
            disabled={
              renameMutation.isPending ||
              name.trim().length === 0 ||
              name.trim() === currentName
            }
            onClick={() => renameMutation.mutate(name.trim())}
          >
            <CheckIcon size={20} />
          </IconButton>
          <IconButton
            label="Cancel rename"
            size={42}
            disabled={renameMutation.isPending}
            onClick={() => {
              setName(currentName);
              setRenaming(false);
            }}
          >
            <XIcon size={20} />
          </IconButton>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <span
            className="text-[11px] font-semibold uppercase tracking-wider"
            style={{ color: "var(--color-hint)" }}
          >
            Manage
          </span>
          <span className="flex-1" />
          <IconButton
            label="Rename project"
            size={42}
            onClick={() => setRenaming(true)}
          >
            <PencilIcon size={19} />
          </IconButton>
          {isOwner ? (
            <IconButton
              label="Delete project"
              variant="danger"
              size={42}
              onClick={() => setConfirmDelete(true)}
            >
              <TrashIcon size={19} />
            </IconButton>
          ) : null}
        </div>
      )}

      {renameMutation.isError ? (
        <p
          className="text-xs"
          style={{ color: "var(--color-destructive)" }}
          role="alert"
        >
          {describeError(renameMutation.error)}
        </p>
      ) : null}

      {confirmDelete ? (
        <div
          className="rounded-[14px] p-3.5 space-y-3"
          style={{
            background:
              "color-mix(in srgb, var(--color-destructive) 10%, transparent)",
            boxShadow:
              "inset 0 0 0 1px color-mix(in srgb, var(--color-destructive) 28%, transparent)",
          }}
        >
          <p className="text-[13.5px] leading-snug">
            Delete <strong>{currentName}</strong>? Its team, links and linked
            chats go too. Your captured notes stay in memory (unfiled). This
            can't be undone.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate()}
              className="flex items-center gap-1.5 text-[14px] font-medium px-4 py-2.5 rounded-full disabled:opacity-40 active:scale-95 transition-transform"
              style={{
                background: "var(--color-destructive)",
                color: "var(--color-button-text)",
              }}
            >
              <TrashIcon size={16} />
              {deleteMutation.isPending ? "Deleting…" : "Delete"}
            </button>
            <button
              type="button"
              disabled={deleteMutation.isPending}
              onClick={() => setConfirmDelete(false)}
              className="text-[14px] px-4 py-2.5 rounded-full active:scale-95 transition-transform"
              style={{
                background: "var(--color-bg)",
                color: "var(--color-text)",
                boxShadow: "inset 0 0 0 1px var(--surface-hairline)",
              }}
            >
              Cancel
            </button>
          </div>
          {deleteMutation.isError ? (
            <p
              className="text-xs"
              style={{ color: "var(--color-destructive)" }}
              role="alert"
            >
              {describeError(deleteMutation.error)}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function CenteredError({
  title,
  message,
  onPrimary,
  primaryLabel,
}: {
  title: string;
  message?: string;
  onPrimary: () => void;
  primaryLabel: string;
}): React.ReactElement {
  return (
    <div
      className="min-h-dvh flex flex-col items-center justify-center gap-3 px-6 text-center"
      style={{ background: "var(--color-bg)", color: "var(--color-text)" }}
    >
      <h2 className="text-lg font-semibold">{title}</h2>
      {message ? (
        <p className="text-sm" style={{ color: "var(--color-hint)" }}>
          {message}
        </p>
      ) : null}
      <button
        type="button"
        onClick={onPrimary}
        className="rounded-full px-4 py-2 text-sm"
        style={{
          background: "var(--color-button)",
          color: "var(--color-button-text)",
        }}
      >
        {primaryLabel}
      </button>
    </div>
  );
}

/* ---------- Notes ------------------------------------------------------- */

function NotesTab({ projectId }: { projectId: string }): React.ReactElement {
  const { data, isLoading, isError, error, refetch } = useQuery<Note[]>({
    queryKey: ["project", projectId, "notes"],
    queryFn: ({ signal }) => listNotes(projectId, {}, signal),
  });

  if (isLoading) return <LoadingScreen message="Loading notes..." />;
  if (isError || !data) return <TabError message={describeError(error)} onRetry={() => void refetch()} />;
  if (data.length === 0) {
    return (
      <EmptyState
        title="No notes yet"
        hint="Forward a voice or text to the bot and attach it to this project."
      />
    );
  }

  return (
    <ul className="space-y-2">
      {data.map((note) => (
        <li
          key={note.id}
          className="rounded-xl p-3"
          style={{ background: "var(--color-secondary-bg)" }}
        >
          <div className="flex items-center gap-2 text-[11px] mb-1" style={{ color: "var(--color-hint)" }}>
            <span
              className="px-2 py-0.5 rounded-full"
              style={{ background: "var(--color-bg)" }}
            >
              {SOURCE_LABEL[note.source_type]}
            </span>
            <RelativeTime iso={note.created_at} />
          </div>
          <p className="text-sm whitespace-pre-wrap">
            {note.transcript ? truncate(note.transcript, 200) : (
              <span style={{ color: "var(--color-hint)" }}>(no transcript)</span>
            )}
          </p>
        </li>
      ))}
    </ul>
  );
}

/* ---------- Tasks ------------------------------------------------------- */

function TasksTab({ projectId }: { projectId: string }): React.ReactElement {
  const queryClient = useQueryClient();
  const tasksKey = ["project", projectId, "tasks"] as const;
  const [editingId, setEditingId] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery<Task[]>({
    queryKey: tasksKey,
    queryFn: ({ signal }) => listTasks(projectId, {}, signal),
  });

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: tasksKey });
    await queryClient.invalidateQueries({ queryKey: ["project", projectId] });
    await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    await queryClient.invalidateQueries({ queryKey: ["feed"] });
  };

  const statusMutation = useMutation({
    mutationFn: (vars: { taskId: string; status: TaskStatus }) =>
      updateTaskStatus(vars.taskId, vars.status),
    onSuccess: invalidate,
  });

  const editMutation = useMutation({
    mutationFn: (vars: { taskId: string; fields: TaskEdit }) =>
      editTask(vars.taskId, vars.fields),
    onSuccess: async () => {
      setEditingId(null);
      await invalidate();
    },
  });

  if (isLoading) return <LoadingScreen message="Loading tasks..." />;
  if (isError || !data) return <TabError message={describeError(error)} onRetry={() => void refetch()} />;
  if (data.length === 0) {
    return (
      <EmptyState
        title="No action items yet"
        hint="Tasks extracted from notes will land here."
      />
    );
  }

  return (
    <ul className="space-y-2">
      {data.map((task) => {
        const isOpen = task.status === "open";
        const inflight =
          statusMutation.isPending && statusMutation.variables?.taskId === task.id;
        return (
          <li
            key={task.id}
            className="rounded-xl p-3"
            style={{
              background: "var(--color-secondary-bg)",
              opacity: isOpen ? 1 : 0.55,
            }}
          >
            <div className="flex items-start gap-2">
              <strong className="flex-1 text-sm leading-snug">{task.title}</strong>
              {task.priority ? (
                <Pill bg={PRIORITY_BG[task.priority]}>{task.priority}</Pill>
              ) : null}
              {!isOpen ? <Pill bg="var(--color-hint)">{task.status}</Pill> : null}
            </div>
            <div
              className="text-[11px] mt-1 flex flex-wrap gap-x-3"
              style={{ color: "var(--color-hint)" }}
            >
              {task.assignee ? <span>{task.assignee}</span> : null}
              {task.deadline ? <span>{task.deadline}</span> : null}
              <RelativeTime iso={task.created_at} />
            </div>

            {editingId === task.id ? (
              <TaskEditForm
                task={task}
                isSaving={editMutation.isPending}
                error={
                  editMutation.isError && editMutation.variables?.taskId === task.id
                    ? describeError(editMutation.error)
                    : null
                }
                onSave={(fields) => editMutation.mutate({ taskId: task.id, fields })}
                onCancel={() => setEditingId(null)}
              />
            ) : isOpen ? (
              <div className="flex gap-2 mt-3">
                <ActionButton
                  primary
                  disabled={inflight}
                  onClick={() => statusMutation.mutate({ taskId: task.id, status: "done" })}
                >
                  {inflight && statusMutation.variables?.status === "done"
                    ? "Saving…"
                    : "Mark done"}
                </ActionButton>
                <ActionButton disabled={inflight} onClick={() => setEditingId(task.id)}>
                  Edit
                </ActionButton>
                <ActionButton
                  disabled={inflight}
                  onClick={() =>
                    statusMutation.mutate({ taskId: task.id, status: "discarded" })
                  }
                >
                  Discard
                </ActionButton>
              </div>
            ) : null}

            {statusMutation.isError &&
            statusMutation.variables?.taskId === task.id ? (
              <p
                className="text-xs mt-2"
                style={{ color: "var(--color-destructive)" }}
                role="alert"
              >
                {describeError(statusMutation.error)}
              </p>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

function TaskEditForm({
  task,
  isSaving,
  error,
  onSave,
  onCancel,
}: {
  task: Task;
  isSaving: boolean;
  error: string | null;
  onSave: (fields: TaskEdit) => void;
  onCancel: () => void;
}): React.ReactElement {
  const [title, setTitle] = useState(task.title);
  const [assignee, setAssignee] = useState(task.assignee ?? "");
  const [deadline, setDeadline] = useState(task.deadline ?? "");
  const [priority, setPriority] = useState<string>(task.priority ?? "");

  const handleSubmit = (): void => {
    const fields: TaskEdit = {};
    const trimmedTitle = title.trim();
    if (trimmedTitle && trimmedTitle !== task.title) {
      fields.title = trimmedTitle;
    }
    const normAssignee = assignee.trim() || null;
    if (normAssignee !== (task.assignee ?? null)) fields.assignee = normAssignee;
    const normDeadline = deadline.trim() || null;
    if (normDeadline !== (task.deadline ?? null)) fields.deadline = normDeadline;
    const normPriority = (priority || null) as TaskEdit["priority"];
    if (normPriority !== (task.priority ?? null)) fields.priority = normPriority;
    if (Object.keys(fields).length === 0) {
      onCancel();
      return;
    }
    onSave(fields);
  };

  return (
    <div className="mt-3 space-y-2">
      <FieldRow label="Title">
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={isSaving}
          className="w-full rounded-lg px-3 py-2 text-sm outline-none"
          style={{
            background: "var(--color-bg)",
            color: "var(--color-text)",
            border: "1px solid var(--color-secondary-bg)",
          }}
        />
      </FieldRow>
      <FieldRow label="Assignee">
        <input
          type="text"
          value={assignee}
          onChange={(e) => setAssignee(e.target.value)}
          disabled={isSaving}
          placeholder="(leave empty to clear)"
          className="w-full rounded-lg px-3 py-2 text-sm outline-none"
          style={{
            background: "var(--color-bg)",
            color: "var(--color-text)",
            border: "1px solid var(--color-secondary-bg)",
          }}
        />
      </FieldRow>
      <FieldRow label="Deadline">
        <input
          type="text"
          value={deadline}
          onChange={(e) => setDeadline(e.target.value)}
          disabled={isSaving}
          placeholder="e.g. Friday, 2026-06-01"
          className="w-full rounded-lg px-3 py-2 text-sm outline-none"
          style={{
            background: "var(--color-bg)",
            color: "var(--color-text)",
            border: "1px solid var(--color-secondary-bg)",
          }}
        />
      </FieldRow>
      <FieldRow label="Priority">
        <select
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
          disabled={isSaving}
          className="w-full rounded-lg px-3 py-2 text-sm outline-none"
          style={{
            background: "var(--color-bg)",
            color: "var(--color-text)",
            border: "1px solid var(--color-secondary-bg)",
          }}
        >
          <option value="">(none)</option>
          <option value="low">low</option>
          <option value="medium">medium</option>
          <option value="high">high</option>
        </select>
      </FieldRow>
      <div className="flex gap-2">
        <ActionButton
          primary
          disabled={isSaving || title.trim().length === 0}
          onClick={handleSubmit}
        >
          {isSaving ? "Saving…" : "Save"}
        </ActionButton>
        <ActionButton disabled={isSaving} onClick={onCancel}>
          Cancel
        </ActionButton>
      </div>
      {error ? (
        <p className="text-xs" style={{ color: "var(--color-destructive)" }} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function FieldRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <label className="block">
      <div
        className="text-[11px] uppercase tracking-wide mb-1"
        style={{ color: "var(--color-hint)" }}
      >
        {label}
      </div>
      {children}
    </label>
  );
}

/* ---------- Decisions / Risks / Files ---------------------------------- */

function DecisionsTab({ projectId }: { projectId: string }): React.ReactElement {
  const { data, isLoading, isError, error, refetch } = useQuery<Decision[]>({
    queryKey: ["project", projectId, "decisions"],
    queryFn: ({ signal }) => listDecisions(projectId, signal),
  });

  if (isLoading) return <LoadingScreen message="Loading decisions..." />;
  if (isError || !data) return <TabError message={describeError(error)} onRetry={() => void refetch()} />;
  if (data.length === 0) {
    return (
      <EmptyState
        title="No decisions captured yet"
        hint="Decisions extracted from notes will appear here."
      />
    );
  }
  return (
    <ul className="space-y-2">
      {data.map((d) => (
        <li
          key={d.id}
          className="rounded-xl p-3"
          style={{ background: "var(--color-secondary-bg)" }}
        >
          <p className="text-sm leading-snug">{d.content}</p>
          <div className="text-[11px] mt-1" style={{ color: "var(--color-hint)" }}>
            <RelativeTime iso={d.created_at} />
          </div>
        </li>
      ))}
    </ul>
  );
}

function RisksTab({ projectId }: { projectId: string }): React.ReactElement {
  const { data, isLoading, isError, error, refetch } = useQuery<Risk[]>({
    queryKey: ["project", projectId, "risks"],
    queryFn: ({ signal }) => listRisks(projectId, signal),
  });

  if (isLoading) return <LoadingScreen message="Loading risks..." />;
  if (isError || !data) return <TabError message={describeError(error)} onRetry={() => void refetch()} />;
  if (data.length === 0) {
    return (
      <EmptyState
        title="No risks captured yet"
        hint="Risks extracted from notes will appear here."
      />
    );
  }
  return (
    <ul className="space-y-2">
      {data.map((r) => (
        <li
          key={r.id}
          className="rounded-xl p-3"
          style={{ background: "var(--color-secondary-bg)" }}
        >
          <div className="flex items-center gap-2 mb-1">
            <Pill bg={SEVERITY_BG[r.severity]}>{r.severity}</Pill>
            <span className="text-[11px]" style={{ color: "var(--color-hint)" }}>
              <RelativeTime iso={r.created_at} />
            </span>
          </div>
          <p className="text-sm">{r.content}</p>
        </li>
      ))}
    </ul>
  );
}

function FilesTab({
  links,
}: {
  links: ProjectDetail["links"];
}): React.ReactElement {
  if (links.length === 0) {
    return (
      <EmptyState
        title="No files or links yet"
        hint="Attach Jira / Figma / Drive / chat links via the bot. PDF + DOCX upload lands in V1.5."
      />
    );
  }
  return (
    <>
      <ul className="space-y-2">
        {links.map((link) => (
          <li
            key={link.id}
            className="rounded-xl p-3"
            style={{ background: "var(--color-secondary-bg)" }}
          >
            <Pill bg="var(--color-bg)">{link.link_type}</Pill>
            <a
              href={link.url}
              target="_blank"
              rel="noreferrer noopener"
              className="block mt-1 text-sm underline truncate"
              style={{ color: "var(--color-link)" }}
            >
              {link.label || link.url}
            </a>
          </li>
        ))}
      </ul>
      <p className="text-xs mt-3" style={{ color: "var(--color-hint)" }}>
        PDF and DOCX uploads land in V1.5.
      </p>
    </>
  );
}

/* ---------- Tiny primitives ------------------------------------------- */

function Pill({
  bg,
  children,
}: {
  bg: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <span
      className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full"
      style={{ background: bg, color: "var(--color-button-text)" }}
    >
      {children}
    </span>
  );
}

function ActionButton({
  primary,
  disabled,
  onClick,
  children,
}: {
  primary?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="px-3 py-1.5 rounded-full text-xs disabled:opacity-40 active:scale-95 transition-transform"
      style={
        primary
          ? {
              background: "var(--color-button)",
              color: "var(--color-button-text)",
            }
          : {
              background: "var(--color-bg)",
              color: "var(--color-text)",
              border: "1px solid var(--color-secondary-bg)",
            }
      }
    >
      {children}
    </button>
  );
}

function TabError({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}): React.ReactElement {
  return (
    <div className="text-center py-6">
      <p className="text-sm mb-2" style={{ color: "var(--color-destructive)" }}>
        {message}
      </p>
      <ActionButton primary onClick={onRetry}>
        Retry
      </ActionButton>
    </div>
  );
}
